import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { useAuth } from '@/hooks/useAuth';
import { useHeldAccounts } from '@/hooks/useHeldAccounts';
import { switchAccount } from '@/lib/activeAccount';
import { apiErrorMessage } from '@/utils/apiError';
import { AppControl } from '../components/AppControl';
import { Card } from '../components/Facts';
import { useAppBinding, useBindApp } from '../hooks';

/** `ABCD-EFGH`, as the app shows it. */
function shownCode(code: string): string {
  const bare = code.replace(/-/g, '').toUpperCase();
  return bare.length === 8 ? `${bare.slice(0, 4)}-${bare.slice(4)}` : bare;
}

// The account chosen on this page, across the reload that switches the tab
// to it. In this tab's sessionStorage, so it's a choice made here: a link
// can't carry it.
const CHOSEN_KEY = 'vpnConnectChosen';

function rememberChosen(code: string) {
  try {
    window.sessionStorage.setItem(CHOSEN_KEY, code);
  } catch {
    // Without storage, the reloaded page asks again.
  }
}

function takeChosen(code: string): boolean {
  try {
    const chosen = window.sessionStorage.getItem(CHOSEN_KEY);
    window.sessionStorage.removeItem(CHOSEN_KEY);
    return chosen === code;
  } catch {
    return false;
  }
}

interface Choice {
  id: string;
  name: string;
  email: string;
}

/**
 * The control page for one run of the Witina app (docs/VpnAccess.md, "Slice 9
 * design"). The app opens it with its link. Nothing is bound until the user
 * picks the account to connect as, from those this browser is signed in to:
 * a tab opened by the app starts on the browser's first account, which may
 * not be the one meant. Then each site the account's policies allow has a
 * toggle, and the app brings each tunnel up and down.
 */
export function VpnConnect() {
  const { code = '' } = useParams();
  const { user } = useAuth();
  const held = useHeldAccounts(!!user);
  const bind = useBindApp();
  const [bindingId, setBindingId] = useState<string>();
  const asked = useRef(false);

  const connect = useCallback(() => {
    asked.current = true;
    bind.mutate(code, {
      onSuccess: (binding) => {
        setBindingId(binding.id);
      },
    });
  }, [bind, code]);

  // Back from switching to the account chosen here: connect as it.
  useEffect(() => {
    if (!asked.current && user && takeChosen(code)) connect();
  }, [code, connect, user]);

  const binding = useAppBinding(bindingId);

  const accounts: Choice[] = (held.data ?? [])
    .filter((a) => a.signedIn)
    .map((a) => ({ id: a.id, name: `${a.firstName} ${a.lastName}`.trim(), email: a.email }));
  if (user && !accounts.some((a) => a.id === user.id)) {
    accounts.unshift({ id: user.id, name: `${user.firstName} ${user.lastName}`.trim(), email: user.email });
  }
  // This tab's account first.
  accounts.sort((a, b) => Number(b.id === user?.id) - Number(a.id === user?.id));

  const connectAs = (account: Choice) => {
    if (account.id === user?.id) {
      connect();
      return;
    }
    rememberChosen(code);
    switchAccount(account.id, `/vpn/connect/${code}`);
  };

  return (
    <PageContainer width="medium">
      <PageHeader title="Witina VPN" subtitle="Turn on the sites you need. Your app connects to each one." />
      {bind.isError ? (
        <Alert variant="destructive">
          <AlertTitle>This link can&rsquo;t be used</AlertTitle>
          <AlertDescription>{apiErrorMessage(bind.error, "Couldn't open the link")}</AlertDescription>
        </Alert>
      ) : binding.data ? (
        <AppControl binding={binding.data} code={shownCode(code)} />
      ) : bind.isPending || bindingId ? (
        <p className="text-xs text-muted-foreground">Connecting to your app…</p>
      ) : (
        <Card
          title="Connect your Witina VPN app"
          description={
            <>
              Code <span className="font-mono">{shownCode(code)}</span>: it should match what your app shows. Only
              continue if your Witina VPN app opened this page; don&rsquo;t use a link anyone else gave you.
            </>
          }
        >
          <p className="text-sm mb-3">Choose the account the app connects as. It can reach the sites that account&rsquo;s policies allow.</p>
          <ul className="space-y-2">
            {accounts.map((account) => (
              <li key={account.id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{account.name || account.email}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {account.email}
                    {account.id === user?.id ? ' · this tab' : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    connectAs(account);
                  }}
                >
                  Connect as {account.email}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </PageContainer>
  );
}
